import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { COLUMN_SELECTOR_CHECKBOX_ID } from '../dataset/column-selector/column-selector.component';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { StudyEditorContext } from '../../study-editor.context';
import { ColumnData } from '../../shared/models/column-data.model';
import { ColumnDataService } from '../../shared/services/column-data.service';
import { JhiLanguageService } from '../../../shared/core-utils';
import { GermplasmDetailsPopupService } from '../../../germplasm-details/germplasm-details-popup.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { InventoryService } from '../../../shared/inventory/service/inventory.service';
import { StudyInstanceModel } from '../../../shared/dataset/model/study-instance.model';
import { StudyInstanceService } from '../../../shared/study/service/study-instance.service';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { StudyEditorService } from '../../study-editor.service';
import { DateHelperService } from '../../../shared/service/date.helper.service';
import { StudyTransactionsRequest, TransactionsSearch } from '../../../shared/inventory/service/study-transaction-request.model';
import { StudyTransactionModel } from '../../../shared/inventory/service/study-transaction.model';
import { TransactionDetailsModalComponent } from '../../shared/modals/transaction-details-modal/transaction-details-modal.component';
import { TransactionAdditionalDetailsModalComponent } from '../../shared/modals/transaction-additional-details-modal/transaction-additional-details-modal.component';
import { Principal } from '../../../shared';
import { MS_CANCEL_PENDING_TRANSACTIONS, VIEW_GERMPLASM_DETAILS_PERMISSION, VIEW_NAMES_PERMISSIONS } from '../../../shared/auth/permissions';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { MAX_NAME_DISPLAY_SIZE } from '../../../app.constants';
import { preventDefaultOnShiftKey } from '../../../field-management/utils';
import {
    getStaticColumnLabelKey,
    STATIC_COLUMN_CREATED_BY_USERNAME,
    STATIC_COLUMN_CREATED_DATE,
    STATIC_COLUMN_DESIGNATION,
    STATIC_COLUMN_ENTRY_NO,
    STATIC_COLUMN_ENTRY_TYPE,
    STATIC_COLUMN_GID,
    STATIC_COLUMN_LOT_AMOUNT,
    STATIC_COLUMN_LOT_NOTES, STATIC_COLUMN_LOT_STORAGE_LOCATION_ABBREVIATION, STATIC_COLUMN_LOT_TRANSACTION_STATUS, STATIC_COLUMN_LOT_TRANSACTION_TYPE, STATIC_COLUMN_LOT_UNIT,
    STATIC_COLUMN_PLOT_NO, STATIC_COLUMN_STOCK_ID, STATIC_COLUMN_TRANSACTION_ID,
    STATIC_COLUMN_TRIAL_INSTANCE
} from '../../../shared/util/static-columns-util';

@Component({
    selector: 'jhi-inventory',
    templateUrl: './inventory.component.html',
    styleUrl: './inventory.component.scss'
})
export class InventoryComponent implements OnInit, OnDestroy {

    readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;

    protected readonly Math = Math;
    readonly CHECKBOX = COLUMN_SELECTOR_CHECKBOX_ID;
    readonly STATIC_COLUMN_TRIAL_INSTANCE = STATIC_COLUMN_TRIAL_INSTANCE;
    readonly STATIC_COLUMN_ENTRY_TYPE = STATIC_COLUMN_ENTRY_TYPE;
    readonly STATIC_COLUMN_GID = STATIC_COLUMN_GID;
    readonly STATIC_COLUMN_DESIGNATION = STATIC_COLUMN_DESIGNATION;
    readonly STATIC_COLUMN_ENTRY_NO = STATIC_COLUMN_ENTRY_NO;
    readonly STATIC_COLUMN_PLOT_NO = STATIC_COLUMN_PLOT_NO;
    readonly STATIC_COLUMN_LOT_STORAGE_LOCATION_ABBREVIATION = STATIC_COLUMN_LOT_STORAGE_LOCATION_ABBREVIATION;
    readonly STATIC_COLUMN_TRANSACTION_ID = STATIC_COLUMN_TRANSACTION_ID;
    readonly STATIC_COLUMN_STOCK_ID = STATIC_COLUMN_STOCK_ID;
    readonly STATIC_COLUMN_CREATED_DATE = STATIC_COLUMN_CREATED_DATE;
    readonly STATIC_COLUMN_CREATED_BY_USERNAME = STATIC_COLUMN_CREATED_BY_USERNAME;
    readonly STATIC_COLUMN_LOT_TRANSACTION_TYPE = STATIC_COLUMN_LOT_TRANSACTION_TYPE;
    readonly STATIC_COLUMN_LOT_TRANSACTION_STATUS = STATIC_COLUMN_LOT_TRANSACTION_STATUS;
    readonly STATIC_COLUMN_LOT_UNIT = STATIC_COLUMN_LOT_UNIT;
    readonly STATIC_COLUMN_LOT_AMOUNT = STATIC_COLUMN_LOT_AMOUNT;
    readonly STATIC_COLUMN_LOT_NOTES = STATIC_COLUMN_LOT_NOTES;
    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;
    protected readonly getStaticColumnLabelKey = getStaticColumnLabelKey;

    @ViewChild('p', { static: false }) columnFilterPopover: NgbPopover;

    displayedColumns: TransactionColumnData[] = [];
    entries: StudyTransactionModel[] = [];

    selectedItems: Map<number, number> = new Map<number, number>();
    lastClickIndex: any;
    numberOfEntries = 0;
    numberOfFilteredEntries: number;
    currentPage = 1;
    pageSize = 50;

    isReloadPending: boolean;
    reloadTimeout: any;
    isTableDataReloadPending: boolean;
    tableDataReloadTimeout: any;
    reloadDelay: number = 800;

    isCategoricalDescriptionView = false;
    environments: StudyInstanceModel[] = [];
    selectedEnvironment: StudyInstanceModel;

    activePopover?: NgbPopover;
    columnDataForFilter: TransactionColumnData;
    columnDataService: ColumnDataService;
    isSelectAll = false;
    from: any;
    to: any;

    hasCancelPendingTransactionsPermission = false;
    hasViewGermplasmDetailsPermission = false;
    hasViewNamesPermission = false;

    subscriptionManager = new SubscriptionsManager();

    constructor(private jhiLanguageService: JhiLanguageService,
                private translateService: TranslateService,
                private inventoryService: InventoryService,
                private studyEditorContext: StudyEditorContext,
                private studyEditorService: StudyEditorService,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private alertService: AlertService,
                private studyInstanceService: StudyInstanceService,
                private modalService: NgbModal,
                private dateHelperService: DateHelperService,
                private principal: Principal) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    async ngOnInit(): Promise<void> {

        this.columnDataService = new ColumnDataService(ColumnDataService.INVENTORY_TABLE_COLUMNS_KEY, String(this.studyEditorContext.studyId));

        this.hasCancelPendingTransactionsPermission = this.principal.hasAnyAuthorityDirect(MS_CANCEL_PENDING_TRANSACTIONS);
        this.hasViewGermplasmDetailsPermission = this.principal.hasAnyAuthorityDirect(VIEW_GERMPLASM_DETAILS_PERMISSION);
        this.hasViewNamesPermission = this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSIONS);

        const enviromentsList = await this.studyInstanceService.listStudyInstances(this.studyEditorContext.studyId).toPromise();
        const allEnvironments = new StudyInstanceModel(-1, 'All environments');
        this.environments = [allEnvironments, ...enviromentsList];

        this.selectedEnvironment = this.environments[0];
        this.loadTable();
    }

    /**
     * Efficiently reload table
     */
    scheduleReload(): void {
        if (this.isReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.reloadTimeout);
        } else {
            this.isReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.reloadTimeout = setTimeout(() => {
            this.loadTable();
            this.isReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    /**
     * Efficiently reload table data
     */
    scheduleTableDataReload(pageIndex, pageSize): void {
        if (this.isTableDataReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.tableDataReloadTimeout);
        } else {
            this.isTableDataReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.tableDataReloadTimeout = setTimeout(() => {
            this.loadTableData(pageIndex, pageSize);
            this.isTableDataReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    async loadTable() {
        await this.loadTableHeader();
        this.loadTableData();
    }

    createColumnData(id: number, displayName: string, visible: boolean, dataTypeCode?: 'NUMERIC_LIST' | 'TEXT' | 'OPTIONS' | 'NUMERIC_RANGE' | 'DATE' | undefined, name?: string, options?: FilterOption[]): TransactionColumnData {
        return {
            displayName: displayName,
            id: id,
            termId: id,
            visible: visible,
            columnSelectorVisible: true,
            selected: true,
            variableTypeId: 0,
            possibleValuesById: undefined,
            possibleValuesByName: undefined,
            enableFilterSorting: true,
            deletable: false,
            hidden: false,
            inputVariables: '',
            isChecked: false,
            name: name,
            alias: '',
            from: null,
            to: null,
            options: options,
            min: null,
            max: null,
            dataTypeCode: dataTypeCode
        };
    }

    async loadTableHeader() {

        const inventoryUnits = await this.inventoryService.queryUnits().toPromise();
        const transactionTypes = await this.inventoryService.queryTransactionTypes().toPromise();
        const transactionStatusTypes = await this.inventoryService.queryTransactionStatusTypes().toPromise();

        const inventoryUnitOptions: FilterOption[] = inventoryUnits.map(unit => {
            return { id: parseInt(unit.id, 10), name: unit.name, checked: true };
        });
        const transactionTypesOptions: FilterOption[] = transactionTypes.map(transactionType => {
            return { id: transactionType.id, name: transactionType.value, checked: true };
        });
        const transactionStatusTypesOptions: FilterOption[] = transactionStatusTypes.map((status) => {
            return { id: status.intValue, name: status.value, checked: true };
        });

        const columns: TransactionColumnData[] = [
            this.createColumnData(this.CHECKBOX, '', true, null, 'check'),
            this.createColumnData(this.STATIC_COLUMN_TRIAL_INSTANCE, 'TRIAL_INSTANCE', true, 'NUMERIC_LIST', 'instanceNo'),
            this.createColumnData(this.STATIC_COLUMN_TRANSACTION_ID, 'TRN_ID', true, 'NUMERIC_LIST', 'transactionId'),
            this.createColumnData(this.STATIC_COLUMN_ENTRY_TYPE, 'ENTRY_TYPE', true, 'TEXT', 'entryType'),
            this.createColumnData(this.STATIC_COLUMN_GID, 'GID', true, 'NUMERIC_LIST', 'lotGid'),
            this.createColumnData(this.STATIC_COLUMN_DESIGNATION, 'DESIGNATION', this.hasViewNamesPermission, 'TEXT', 'lotDesignation'),
            this.createColumnData(this.STATIC_COLUMN_ENTRY_NO, 'ENTRY_NO', true, 'NUMERIC_LIST', 'entryNo'),
            this.createColumnData(this.STATIC_COLUMN_PLOT_NO, 'PLOT_NO', true, 'NUMERIC_LIST', 'plotNo'),
            this.createColumnData(this.STATIC_COLUMN_LOT_STORAGE_LOCATION_ABBREVIATION, 'STORAGE LOCATION ABBR', true,'TEXT', 'lotLocationAbbr'),
            this.createColumnData(this.STATIC_COLUMN_STOCK_ID, 'STOCK_ID', true, 'TEXT', 'lotStockId'),
            this.createColumnData(this.STATIC_COLUMN_CREATED_DATE, 'CREATED', true, 'DATE', 'createdDate'),
            this.createColumnData(this.STATIC_COLUMN_CREATED_BY_USERNAME, 'USERNAME', true, 'TEXT', 'createdByUsername'),
            this.createColumnData(this.STATIC_COLUMN_LOT_TRANSACTION_TYPE, 'TYPE', true, 'OPTIONS', 'transactionType', transactionTypesOptions),
            this.createColumnData(this.STATIC_COLUMN_LOT_TRANSACTION_STATUS, 'STATUS', true, 'OPTIONS', 'transactionStatus', transactionStatusTypesOptions),
            this.createColumnData(this.STATIC_COLUMN_LOT_UNIT, 'UNITS', true, 'OPTIONS', 'lotUnitName', inventoryUnitOptions),
            this.createColumnData(this.STATIC_COLUMN_LOT_AMOUNT, 'AMOUNT', true, 'NUMERIC_RANGE', 'amount'),
            this.createColumnData(this.STATIC_COLUMN_LOT_NOTES, 'NOTES', true, 'TEXT', 'notes')
        ];

        this.displayedColumns = this.columnDataService.displayedColumns = columns;
        this.displayedColumns = this.columnDataService.applyColumnsState() as TransactionColumnData[];

    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.scheduleTableDataReload((page - 1), this.pageSize);
    }

    onPageSizeChange($event: Event) {
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    loadTableData(pageIndex = 0, pageSize = 50): void {

        const request = this.createRequest();
        const sort = this.getSort();

        this.subscriptionManager.add = this.inventoryService.searchStudyTransactions(this.studyEditorContext.studyDetails.id, request, pageIndex, this.pageSize, sort).subscribe(
            (paginatedResponse) => {
                this.entries = paginatedResponse.data;
                this.numberOfEntries = paginatedResponse.totalCount;
                this.numberOfFilteredEntries = paginatedResponse.filteredCount;
                this.studyEditorContext.germplasmListExists = this.numberOfEntries > 0;
                this.studyEditorContext.studyDetails.numberOfEntries = this.numberOfEntries;
            }
        );
    }

    trackRowsById(entry: StudyTransactionModel) {
        return entry.transactionId;
    }

    getSort(): string {

        const sort = this.displayedColumns.reduce((sort, column) => {
            if (column.sortingAsc !== null && column.sortingAsc !== undefined) {
                return column.name + ',' + (column.sortingAsc ? 'asc' : 'desc');
            }
            return sort;
        }, '');

        return sort;
    }

    createRequest(): StudyTransactionsRequest {

        const filter: TransactionsSearch = {};
        const request: StudyTransactionsRequest = {
            transactionsSearch: filter
        };

        if (this.selectedEnvironment.instanceId > 0) {
            request.instanceNoList = [this.selectedEnvironment.instanceNumber];
        }

        this.displayedColumns.forEach(column => {
            if (column.isFiltered) {
                if (column.id === this.STATIC_COLUMN_TRIAL_INSTANCE) {
                    request.instanceNoList = InventoryComponent.toNumberArray(column.query.split(','));
                }
                if (column.id === this.STATIC_COLUMN_TRANSACTION_ID) {
                    request.transactionsSearch.transactionIds = InventoryComponent.toNumberArray(column.query.split(','));
                }
                if (column.id === this.STATIC_COLUMN_ENTRY_TYPE) {
                    request.entryType = column.query;
                }
                if (column.id === this.STATIC_COLUMN_GID) {
                    request.transactionsSearch.gids = InventoryComponent.toNumberArray(column.query.split(','));
                }
                if (column.id === this.STATIC_COLUMN_DESIGNATION) {
                    request.transactionsSearch.designation = column.query;
                }
                if (column.id === this.STATIC_COLUMN_ENTRY_NO) {
                    request.entryNoList = InventoryComponent.toNumberArray(column.query.split(','));
                }
                if (column.id === this.STATIC_COLUMN_PLOT_NO) {
                    request.plotNoList = InventoryComponent.toNumberArray(column.query.split(','));
                }
                if (column.id === this.STATIC_COLUMN_LOT_STORAGE_LOCATION_ABBREVIATION) {
                    request.transactionsSearch.lotLocationAbbr = column.query;
                }
                if (column.id === this.STATIC_COLUMN_STOCK_ID) {
                    request.transactionsSearch.stockId = column.query;
                }
                if (column.id === this.STATIC_COLUMN_CREATED_DATE) {
                    if (column.from) {
                        request.transactionsSearch.createdDateFrom = this.dateHelperService.convertFormattedNgbDateToString(column.from, '-');
                    }
                    if (column.to) {
                        request.transactionsSearch.createdDateTo = this.dateHelperService.convertFormattedNgbDateToString(column.to, '-');
                    }
                }
                if (column.id === this.STATIC_COLUMN_CREATED_BY_USERNAME) {
                    request.transactionsSearch.createdByUsername = column.query;
                }
                if (column.id === this.STATIC_COLUMN_LOT_TRANSACTION_TYPE) {
                    const selectedOptions = column.options.filter((option) => option.checked);
                    if (selectedOptions.length) {
                        const value = selectedOptions.map(option => option.id);
                        request.transactionsSearch.transactionTypes = value;
                    }
                }
                if (column.id === this.STATIC_COLUMN_LOT_TRANSACTION_STATUS) {
                    const selectedOptions = column.options.filter((option) => option.checked);
                    if (selectedOptions.length) {
                        const value = selectedOptions.map(option => option.id);
                        request.transactionsSearch.transactionStatus = value;
                    }
                }
                if (column.id === this.STATIC_COLUMN_LOT_UNIT) {
                    const selectedOptions = column.options.filter((option) => option.checked);
                    if (selectedOptions.length) {
                        const value = selectedOptions.map(option => option.id);
                        request.transactionsSearch.unitIds = value;
                    }
                }
                if (column.id === this.STATIC_COLUMN_LOT_AMOUNT) {
                    if (column.min) {
                        request.transactionsSearch.minAmount = column.min;
                    }
                    if (column.max) {
                        request.transactionsSearch.maxAmount = column.max;
                    }
                }
                if (column.id === this.STATIC_COLUMN_LOT_NOTES) {
                    request.transactionsSearch.notes = column.query;
                }
            }
        });

        return request;
    }

    clearSelected() {
        this.selectedItems.clear();
    }

    selectAll(isSelectAll) {
        this.isSelectAll = isSelectAll;
        // Hide the checkbox column when select all is true
        if (isSelectAll) {
            this.displayedColumns.find(column => column.id === this.CHECKBOX).selected = false;
        } else {
            this.displayedColumns.find(column => column.id === this.CHECKBOX).selected = true;
        }
        if (this.isSelectAll) {
            this.clearSelected();
        }
    }

    isSelected(germplasmStudySourceId: number): boolean {
        return this.selectedItems.has(germplasmStudySourceId) || this.isSelectAll;
    }

    toggleSelect($event, index: number, studyTransactionModel: StudyTransactionModel, checkbox = false) {

        if (this.isSelectAll) {
            return;
        }

        if ($event.ctrlKey || $event.metaKey) {
            $event.preventDefault();
        }

        if (!checkbox) {
            $event.preventDefault();
        }

        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems.clear();
        }

        let items: StudyTransactionModel[] = [];
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1;
            const min = Math.min(this.lastClickIndex, index);
            items = this.entries.slice(min, max);
        } else {
            items = [studyTransactionModel];
            this.lastClickIndex = index;
        }

        const isClickedItemSelected = this.selectedItems.has(studyTransactionModel.transactionId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.transactionId);
            } else {
                this.selectedItems.set(item.transactionId, item.lot.lotId);
            }
        }

    }

    trackColumnsById(column: TransactionColumnData) {
        return column.id;
    }

    // Filters

    showColumnFilterPopover(column: TransactionColumnData, p: NgbPopover) {
        this.columnDataForFilter = column;
        p.open({
            column: column
        });
    }

    sortColumn(column: TransactionColumnData, ascending: boolean): void {

        // Clear sorting state for all displayed columns to ensure only one column can be sorted at a time
        this.resetSortingsOfDisplayedColumns();

        column.sortingAsc = ascending;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    filterByColumn(column: TransactionColumnData) {
        this.clearSelected();
        column.isFiltered = true;
        this.columnFilterPopover.close();
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetFilterByColumn(column: TransactionColumnData) {
        this.clearSelected();
        column.isFiltered = false;
        column.query = '';
        column.sortingAsc = null;
        column.options?.forEach(value => {
            value.checked = true;
        });
        column.isSelectAll = false;
        column.from = null;
        column.to = null;
        column.min = null;
        column.max = null;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetSortingsOfDisplayedColumns() {
        this.displayedColumns.forEach(column => {
            column.sortingAsc = null;
        });
    }

    filterSelectAll(column: TransactionColumnData, isSelectAll: boolean) {
        if (column.options) {
            column.options.forEach(value => {
                value.checked = isSelectAll;
            });
        }
    }

    cancelPendingTransactions() {
        if (!this.validateSelection()) {
            return;
        }
        this.confirmTransactionsForCancellation();
    };

    confirmTransactionsForCancellation() {
        const request: StudyTransactionsRequest = this.createRequest();
        if (!this.isSelectAll) {
            request.transactionsSearch.transactionIds = Array.from(this.selectedItems.keys());
        }
        this.inventoryService.searchStudyTransactions(this.studyEditorContext.studyId, request).toPromise().then((transactionsTable) => {
            const numberOfItemsSelected = transactionsTable.data.length;
            if (numberOfItemsSelected) {
                // Check that all selected items have Pending Status
                const allSelectedItemsPending = transactionsTable.data.every((item) => {
                    return item.transactionStatus === 'Pending';
                });

                if (allSelectedItemsPending) {
                    const selectedItemIds = transactionsTable.data.map((item) => {
                        return item.transactionId;
                    });

                    const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
                    confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.inventory.confirm-cancellation-pending-transactions', { param: numberOfItemsSelected });
                    confirmModalRef.result.then(() => {
                        this.inventoryService.cancelStudyTransaction(this.studyEditorContext.studyId, { itemIds: selectedItemIds }).toPromise().then(() => {
                            this.alertService.success('study-editor.inventory.cancel-pending-transactions-successful');
                            this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'inventory');
                        });
                    });
                } else {
                    this.alertService.error('study-editor.inventory.cancel-pending-transactions-invalid-status');
                }
            }
        });
    }

    validateSelection() {
        if (this.selectedItems.size === 0 && !this.isSelectAll) {
            this.alertService.error('study-editor.crosses-and-selections.no-germplasm-error');
            return false;
        }
        return true;
    }

    private static toNumberArray(list: string[]) {
        if (list) {
            return list.map((item) => {
                return parseInt(item, 10);
            });
        }
        return [];
    }

    isApplyAllEnabled(columnDataForFilter: TransactionColumnData): boolean {
        if (columnDataForFilter.dataTypeCode === 'DATE' && columnDataForFilter.from) {
            return true;
        }
        if (columnDataForFilter.dataTypeCode === 'OPTIONS' && columnDataForFilter.options.find((option) => option.checked)) {
            return true;
        }
        if (columnDataForFilter.dataTypeCode === 'NUMERIC_RANGE' && columnDataForFilter.min && columnDataForFilter.max) {
            return true;
        }
        if (columnDataForFilter.query) {
            if (columnDataForFilter.dataTypeCode === 'NUMERIC_LIST') {
                const list = InventoryComponent.toNumberArray(columnDataForFilter.query.split(','));
                return list.length > 0;
            } else {
                return true;
            }
        }
        return false;
    }

    getInstanceNumberDisplay(entry: StudyTransactionModel): string {
        return entry.observationUnits[0].instanceNo
            + (entry.observationUnits
                .map(ou => ou.instanceNo)
                .filter((item, pos, self) => {
                    // remove dups
                    return self.indexOf(item) === pos;
                })
                .length > 1 ? 'and more...' : '');
    }

    onEnvironmentChange($event: any) {
        this.selectedEnvironment = $event;
        this.scheduleTableDataReload(this.currentPage - 1, this.pageSize);
    }

    openViewTransactionsModal(gid: number, lotId: number) {
        const modalRef = this.modalService.open(TransactionDetailsModalComponent as Component, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.gid = gid;
        modalRef.componentInstance.lotId = lotId;
    }

    openTransactionAdditinalDetailsModal(entry: StudyTransactionModel) {
        const modalRef = this.modalService.open(TransactionAdditionalDetailsModalComponent as Component, { size: 'md', backdrop: 'static' });
        modalRef.componentInstance.studyTransactionModel = entry;
        modalRef.componentInstance.transactionObservationUnitModels = entry.observationUnits;
    }

    onColumnSelectorDeselectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = false;
            }
        });
    }

    onColumnSelectorSelectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = true;
            }
        });
    }

    onColumnSelectorReset() {
        this.onColumnSelectorSelectedAll();
        this.scheduleReload();
    }

    clearFilters() {
        this.clearSelected();
        this.displayedColumns.forEach(column => {
            this.resetFilterByColumn(column);
        });
    }

    protected onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.entries.forEach((item) => this.selectedItems.delete(item.transactionId));
        } else {
            // check remaining items
            this.entries.forEach((item) => this.selectedItems.set(item.transactionId, item.lot.lotId));
        }
    }

    protected isPageSelected() {
        return this.selectedItems && this.selectedItems.size && this.entries.every((item) => this.selectedItems.has(item.transactionId));
    }
}

class TransactionColumnData implements ColumnData {
    // Required properties from ObservationVariable
    termId: number;
    name: string;
    alias: string;

    displayName: string;
    visible: boolean;
    columnSelectorVisible: boolean;
    selected: boolean;
    variableTypeId: number;
    id: number;
    sortingAsc?: boolean;
    isFiltered?: boolean;
    query?: string;
    isSelectAll?: boolean;
    possibleValuesById: {};
    possibleValuesByName: {};
    enableFilterSorting: boolean;
    deletable: boolean;
    hidden: boolean;
    inputVariables: string;
    isChecked: boolean;

    // Additional Column
    from: any;
    to: any;
    options: FilterOption[];
    min: number;
    max: number;
    dataTypeCode: 'NUMERIC_LIST' | 'TEXT' | 'OPTIONS' | 'NUMERIC_RANGE' | 'DATE' | undefined;
}

class FilterOption {
    id: number;
    name: string;
    checked: boolean;
}



